import { createApp } from 'vue';
import ToastNotification from '@/components/utils/ToastNotification.vue';

class ToastManager {
  constructor() {
    this.toasts = [];
    this.container = null;
    this.app = null;
    this.init();
  }

  init() {
    // Criar container se não existir
    if (!this.container) {
      this.container = document.createElement('div');
      this.container.id = 'toast-container';
      this.container.className = 'toast-container';
      document.body.appendChild(this.container);
    }
  }

  show(options = {}) {
    const {
      message = '',
      type = 'info', // success, error, warning, info
      duration = 4000,
      position = 'top-right',
      ...props
    } = options;

    const toastId = Date.now() + Math.random();
    
    // Criar elemento para este toast
    const toastElement = document.createElement('div');
    this.container.appendChild(toastElement);

    // Criar app Vue para o toast
    const app = createApp(ToastNotification, {
      message,
      type,
      toastId,
      position,
      onRemove: () => this.remove(toastId, app, toastElement),
      ...props
    });

    const instance = app.mount(toastElement);

    // Armazenar referência
    this.toasts.push({
      id: toastId,
      app,
      element: toastElement,
      instance
    });

    // Mostrar o toast
    instance.show();

    // Auto remover após duração especificada
    if (duration > 0) {
      setTimeout(() => {
        this.remove(toastId, app, toastElement);
      }, duration);
    }

    return toastId;
  }

  remove(toastId, app, element) {
    // Remover da lista
    this.toasts = this.toasts.filter(toast => toast.id !== toastId);
    
    // Desmontar app e remover elemento
    if (app) {
      app.unmount();
    }
    if (element && element.parentNode) {
      element.parentNode.removeChild(element);
    }
  }

  removeAll() {
    this.toasts.forEach(toast => {
      if (toast.app) {
        toast.app.unmount();
      }
      if (toast.element && toast.element.parentNode) {
        toast.element.parentNode.removeChild(toast.element);
      }
    });
    this.toasts = [];
  }

  // Métodos de conveniência
  success(message, options = {}) {
    return this.show({ message, type: 'success', ...options });
  }

  error(message, options = {}) {
    return this.show({ message, type: 'error', duration: 6000, ...options });
  }

  warning(message, options = {}) {
    return this.show({ message, type: 'warning', duration: 5000, ...options });
  }

  info(message, options = {}) {
    return this.show({ message, type: 'info', ...options });
  }
}

// Instância global
const toastManager = new ToastManager();

// Composable
export function useToast() {
  return {
    toast: toastManager,
    success: (message, options) => toastManager.success(message, options),
    error: (message, options) => toastManager.error(message, options),
    warning: (message, options) => toastManager.warning(message, options),
    info: (message, options) => toastManager.info(message, options),
    removeAll: () => toastManager.removeAll()
  };
}

// Exportar instância global para compatibilidade
export const toast = {
  success: (message, options) => toastManager.success(message, options),
  error: (message, options) => toastManager.error(message, options),
  warning: (message, options) => toastManager.warning(message, options),
  info: (message, options) => toastManager.info(message, options),
  show: (options) => toastManager.show(options),
  removeAll: () => toastManager.removeAll()
};

// Instalar globalmente
export function installToast(app) {
  app.config.globalProperties.$toast = toast;
  app.provide('toast', toast);
}

export default toastManager; 